import * as React from "react";
import TextField from "@mui/material/TextField";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";

import { useDispatch, useSelector } from "react-redux";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import Checkbox from "@mui/material/Checkbox";
import { Box, FormControlLabel, Chip } from "@mui/material";
import { inarixColors } from "inarix-theme";

import useMediaQuery from "@mui/material/useMediaQuery";
import ListSubheader from "@mui/material/ListSubheader";
import { useTheme } from "@mui/material/styles";
import { VariableSizeList, ListChildComponentProps } from "react-window";
import Typography from "@mui/material/Typography";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { State } from "../../../../redux/slices";
import {
  setFiltersConfig,
  UsersSBFilters,
  UsersSBFilterValues,
} from "../../../../redux/slices/userGroups/userGroupsSliceMultipleTables";
import { DashboardLocIdInfo } from "../../../../redux/actions/dashboard/dashboardTypes";
import { FiltersUsersTableObject } from "../filterOptions/Helpers";

import {
  fetchSBActionsNames,
  fetchSBSites,
} from "../../../../redux/actions/users/userAsyncActions";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const SetActions = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  return {
    fetchActionsAndLocations: (typeOfAutocomplete: string) => {
      if (typeOfAutocomplete === "locations") {
        dispatch(fetchSBSites());
      }
      if (typeOfAutocomplete === "actions") {
        dispatch(fetchSBActionsNames());
      }
    },

    selectFilters: (
      value: DashboardLocIdInfo[],
      usersSBFilters: UsersSBFilters,
      typeOfAutocomplete: string
    ) => {
      if (typeOfAutocomplete === "locations") {
        const values = value?.map((l: any) => {
          return l.id;
        });

        dispatch(
          setFiltersConfig({
            ...usersSBFilters,
            locations: { valuesSelected: value },
            filtersLocation: FiltersUsersTableObject(
              "geoPointName",
              values,
              "="
            ),
          })
        );
      }
      if (typeOfAutocomplete === "actions") {
        const values = value?.map((l: any) => {
          return l.id;
        });
        dispatch(
          setFiltersConfig({
            ...usersSBFilters,
            actions: { valuesSelected: value },
            filterActions: FiltersUsersTableObject("actionName", values, "="),
          })
        );
      }

      if (typeOfAutocomplete === "varieties") {
        const values = value?.map((l) => {
          return l.name;
        });
        dispatch(
          setFiltersConfig({
            ...usersSBFilters,
            varieties: { valuesSelected: value },
            filterVarieties: FiltersUsersTableObject("varieties", values, "="),
          })
        );
      }
    },

    setSelectAll: (
      setSilos: boolean,
      usersSBFilters: UsersSBFilters,
      value: DashboardLocIdInfo[],
      typeOfAutocomplete: string
    ) => {
      if (typeOfAutocomplete === "locations") {
        dispatch(
          setFiltersConfig({
            ...usersSBFilters,
            locations: {
              allValuesSelected: setSilos,
              valuesSelected: value,
            },
            filtersLocation: FiltersUsersTableObject(
              "geoPointName",
              value.map((l: any) => l.id),
              "="
            ),
          })
        );
      }
      if (typeOfAutocomplete === "actions") {
        dispatch(
          setFiltersConfig({
            ...usersSBFilters,
            actions: {
              allValuesSelected: setSilos,
              valuesSelected: value,
            },

            filterActions: FiltersUsersTableObject(
              "actionName",
              value.map((l: any) => l.id),
              "="
            ),
          })
        );
      }
      if (typeOfAutocomplete === "varieties") {
        dispatch(
          setFiltersConfig({
            ...usersSBFilters,
            varieties: {
              allValuesSelected: setSilos,
              valuesSelected: value,
            },
            filterUsers: FiltersUsersTableObject(
              "varieties",
              value.map((l: any) => l.id),
              "="
            ),
          })
        );
      }
    },

    resetSelectAll: (
      usersSBFilters: UsersSBFilters,
      typeOfAutocomplete: string
    ) => {
      if (typeOfAutocomplete === "locations") {
        dispatch(
          setFiltersConfig({
            ...usersSBFilters,
            locations: {
              allValuesSelected: false,
              valuesSelected: [],
            },
            filtersLocation: undefined,
          })
        );
      }
      if (typeOfAutocomplete === "actions") {
        dispatch(
          setFiltersConfig({
            ...usersSBFilters,
            actions: {
              allValuesSelected: false,
              valuesSelected: [],
            },
            filterActions: undefined,
          })
        );
      }
      if (typeOfAutocomplete === "varieties") {
        dispatch(
          setFiltersConfig({
            ...usersSBFilters,
            users: {
              allValuesSelected: false,
              valuesSelected: [],
            },
            filterUsers: undefined,
          })
        );
      }
    },
  };
};

const LISTBOX_PADDING = 8;

export function renderRow(props: ListChildComponentProps) {
  const { data, index, style } = props;

  const dataSet = data[index];
  const inlineStyle = {
    ...style,
    top: (style.top as number) + LISTBOX_PADDING,
  };

  if (dataSet.hasOwnProperty("group")) {
    return (
      <ListSubheader key={dataSet.key} component="div" style={inlineStyle}>
        {dataSet.group}
      </ListSubheader>
    );
  }

  return (
    <Typography component="li" {...dataSet[0]} noWrap style={inlineStyle}>
      <Checkbox
        icon={icon}
        checkedIcon={checkedIcon}
        style={{ marginRight: 8 }}
        checked={dataSet[2]}
      />
      {`${dataSet[1]}`}
    </Typography>
  );
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data: any) {
  const ref = React.useRef<VariableSizeList>(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

export const ListboxComponent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLElement>
>(function ListboxComponent(props, ref) {
  const { children, ...other } = props;
  const itemData: any[] = [];
  (children as any[]).forEach((item: any) => {
    itemData.push(item);
    itemData.push(...(item.children || []));
  });

  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up("sm"), {
    noSsr: true,
  });
  const itemCount = itemData.length;
  const itemSize = smUp ? 36 : 48;

  const getChildSize = (child: any) => {
    if (child.hasOwnProperty("group")) {
      return 48;
    }

    return itemSize;
  };

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
  };

  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType as any}
          innerElementType="ul"
          itemSize={(index: any) => getChildSize(itemData[index])}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

const AutocompleteExtraFiltersInput = (
  token: string,
  typeOfAutocomplete: string,
  dataOptions: DashboardLocIdInfo[],
  allValuesSelected: boolean,
  valuesSelected: UsersSBFilterValues[],
  t: (arg0: string) => string
) => {
  const {
    selectFilters,
    setSelectAll,
    resetSelectAll,
    fetchActionsAndLocations,
  } = SetActions();
  const { usersSBFilters } = useSelector(
    (state: State) => state.CreateTablesUsersReducer
  );
  const { loading } = useSelector((state: State) => state.UsersReducer);

  const TypeOfLabel = () => {
    if (typeOfAutocomplete === "locations") {
      return t("Data.Search_sites");
    }
    if (typeOfAutocomplete === "actions") {
      return t("Data.Search_actions");
    }
    if (typeOfAutocomplete === "users") {
      return t("Data.Search_users");
    }
  };

  const transform = Array.isArray(dataOptions) ? dataOptions.map((c: DashboardLocIdInfo) => c) : [];

  return (
    <Autocomplete
      sx={{
        overflowY: valuesSelected.length > 2 ? "scroll" : "hidden",
        height: valuesSelected.length > 2 ? "120px" : "60px",
        border: 1,
        borderColor: inarixColors.secondary_text_inarix,
        borderRadius: "5px",
        width: 350,

        "&:hover": {
          border: `1.3px solid ${inarixColors.light_inarix}`,
          color: "gray",
        },
      }}
      filterOptions={(options, params) => {
        const filter = createFilterOptions();
        const filtered = filter(options, params as any);
        return [
          { name: t("Dashboard.setAllLocations"), id: 0 },
          ...filtered,
        ] as any;
      }}
      disabled={loading}
      className="autocomplete-dashboard mb-5"
      id={"autocomplete_with_select_all"}
      multiple
      disableListWrap={true}
      options={Array.isArray(dataOptions) ? dataOptions : []}
      disableCloseOnSelect
      value={valuesSelected as any}
      getOptionLabel={(option: DashboardLocIdInfo) => {
        if (typeOfAutocomplete === "actions") {
          return `${option.name} ${(option).cerealType || ""}`;
        }
        return option.name;
      }}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      onChange={(_e, value, reason) => {
        if (reason === "clear" || reason === "removeOption")
          resetSelectAll(usersSBFilters, typeOfAutocomplete);

        if (reason === "selectOption" && value.length === (Array.isArray(dataOptions) ? dataOptions.length : 0))
          setSelectAll(true, usersSBFilters, transform, typeOfAutocomplete);
        selectFilters(value, usersSBFilters, typeOfAutocomplete);
      }}
      renderInput={(params) => (
        <TextField
          sx={{
            "& .MuiInput-underline:after": {
              borderColor: "transparent",
            },

            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: "transparent",
              },
              "&:hover fieldset": {
                borderColor: "transparent",
              },
              "&.Mui-focused fieldset": {
                borderColor: "transparent",
              },
            },
          }}
          {...params}
          onClick={() => fetchActionsAndLocations(typeOfAutocomplete)}
          placeholder={TypeOfLabel()}
        />
      )}
      renderOption={(props, option, { selected }) => {
        if (option.id === 0) {
          return (
            <Box
              key={option.id}
              onMouseDown={(e) => e.preventDefault()}
              pl={1.5}
              py={0.5}
              sx={{
                position: "sticky",
                top: "-1rem",
                background: "#fff",
                height: "60px",
                zIndex: "2",
                borderBottom: "1px solid",
              }}
            >
              <FormControlLabel
                onClick={(e) => {
                  if (Array.isArray(dataOptions) && dataOptions.length >= 1) {
                    e.preventDefault();
                    !allValuesSelected
                      ? setSelectAll(
                          true,
                          usersSBFilters,
                          transform,
                          typeOfAutocomplete
                        )
                      : resetSelectAll(usersSBFilters, typeOfAutocomplete);
                  }
                }}
                disabled={!Array.isArray(dataOptions) || dataOptions.length === 0}
                label={t("Dashboard.setAllLocations")}
                control={
                  <Checkbox
                    disabled={!Array.isArray(dataOptions) || dataOptions.length === 0}
                    checked={allValuesSelected}
                  />
                }
              />
            </Box>
          );
        }

        return (
          <li {...props}>
            <Checkbox
              key={option.id}
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 8 }}
              checked={
                option.id === 0
                  ? !!(
                      valuesSelected.filter((x) => x.id !== undefined)
                        .length === (Array.isArray(dataOptions) ? dataOptions.length : 0)
                    )
                  : selected
              }
            />

            <div>{`${
              typeOfAutocomplete === "actions"
                ? `${option.name} (${option.cerealType})`
                : option.name
            }`}</div>
          </li>
        );
      }}
      renderTags={(list) => {
        if (list.length === 1) {
          return (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {list.map((value: any) => (
                <Chip key={value.id} label={value.name} />
              ))}
            </Box>
          );
        }

        if (list.length > 1) {
          return (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              <Chip
                label={`${list[0].name} ${
                  list.length >= 2
                    ? `+ ${list.length - 1} ${t("Common_options.others")}`
                    : ""
                }  `}
              />
            </Box>
          );
        }
      }}
      renderGroup={(params) => params as unknown as React.ReactNode}
    />
  );
};
export default AutocompleteExtraFiltersInput;
